import { defineStore } from "pinia";
import { ref, computed } from "vue";

/**
 * useUserStore - 專業級身分與忠誠度管理
 * 解決方案：
 * 1. 導入 TMC 等級的 Traveler Profile (包含 TSA & Loyalty)。
 * 2. 實作平滑的身分切換 (Adult to Student) 觸發全站變價。
 * 3. 預留 Saved Travelers 導入介面，減少結帳摩擦。
 */

export interface SavedTraveler {
  id: string;
  firstName: string;
  lastName: string;
  dob: string;
  passportNumber?: string;
  ktn?: string; // Known Traveler Number
  redressNumber?: string;
  loyaltyPrograms: Array<{ airline: string; number: string }>;
}

export const useUserStore = defineStore("scholarUser", () => {
  // --- 狀態 (State) ---
  const verificationStatus = ref<
    "unverified" | "pending" | "verified" | "failed"
  >("unverified");
  const showVerificationModal = ref(false);
  const userProfile = ref<{
    email: string;
    name?: string;
    savedTravelers: SavedTraveler[];
  } | null>(null);
  const currency = ref("USD");

  // --- 計算屬性 (Getters) ---
  const isStudent = computed(() => verificationStatus.value === "verified");

  // 💡 解決方案：為 AEO/GEO 提供身分快照，增加搜尋權威
  const authContext = computed(() => ({
    isVerified: isStudent.value,
    tier: isStudent.value ? "ScholarGold" : "General",
    currency: currency.value,
  }));

  // --- 動作 (Actions) ---
  const openVerification = () => (showVerificationModal.value = true);

  // 專業驗證邏輯：對接 BFF 進行 .edu 深度校驗
  const verifyStudent = async (email: string) => {
    verificationStatus.value = "pending";
    try {
      // 實務串接：await $fetch('/api/bff/auth/verify', { method: 'POST', body: { email } })
      await new Promise((r) => setTimeout(r, 1200));
      verificationStatus.value = "verified";
      userProfile.value = {
        email,
        name: email.split("@")[0],
        savedTravelers: [],
      };
      return true;
    } catch (e) {
      verificationStatus.value = "failed";
      return false;
    }
  };

  // 💡 解決方案：從 BFF 獲取常用旅客清單 (Checkout 快速填表用)
  const fetchSavedTravelers = async () => {
    if (!userProfile.value) return;
    // 模擬從資料庫讀取
    userProfile.value.savedTravelers = [
      {
        id: "1",
        firstName: "MING",
        lastName: "WANG",
        dob: "1998-05-20",
        ktn: "12345678",
        loyaltyPrograms: [{ airline: "BR", number: "BR12345" }],
      },
    ];
  };

  return {
    verificationStatus,
    showVerificationModal,
    userProfile,
    currency,
    isStudent,
    authContext,
    openVerification,
    verifyStudent,
    fetchSavedTravelers,
    toggleVerification: () => {
      verificationStatus.value = isStudent.value ? "unverified" : "verified";
    },
  };
});
